import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { Badge, Button, Col, Form, Modal, Row, Card, Table } from 'react-bootstrap'
import { GET_ONE_CONTACT_API } from '../../../gql/queries/contactsApiQueries'
import { IResOneContact, NodeContact } from '../../../types/contactsApi'
import { parseId } from '../../../helpers'
import moment from 'moment'
// import { DateInput } from '../../../components/DateInput'
import { SignatureFormsEntriesTimeline } from '../../signatureFormsEntries/components/SignatureFormsEntriesTimeline'
import { GET_ALL_SIGNATURE_FORMS_ENTRIES_API } from '../../../gql/queries/signatureFormsEntriesApiQueries'
import { IResSignatureFormsEntriesAPI } from '../../../types/signatureFormsEntriesApi'
import { BadgeStatePolicy } from '../../onePolicy/components/BadgeStatePolicy'
import { getDocumentVariant, getPaymentVariant } from '../../../helpers/consts'
import { UserAvatar } from '../../../components/UserAvatar'
import { AgentInfoTooltip } from '../../shared/AgentInfoTooltip'
import { GET_ALL_POLICIES_STATUSES } from '../../../gql/queries/policiesQueries'
import { getColorByStatus, getTitleByStatus } from '../../shared/utils'
import { AllPolicyStatusesData } from '../../../types/policies'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'

export const ModalPreview: React.FC<{
  idContact: string | null
  onClose: () => void
}> = ({ idContact, onClose }) => {
  const [infoPreview, setInfoPreview] = useState<NodeContact | null>(null)

  const { data, loading, error } = useQuery<IResOneContact>(GET_ONE_CONTACT_API, {
    fetchPolicy: 'no-cache',
    variables: { id: parseId(idContact ?? '') },
    skip: !idContact,
  })

  const { data: dataPoliciesStatuses } = useQuery<AllPolicyStatusesData>(
    GET_ALL_POLICIES_STATUSES,
    {
      variables: { first: 100 },
      fetchPolicy: 'no-cache',
    }
  )

  const { data: dataEntries, loading: loadingEntries, refetch: refetchEntries } = useQuery<IResSignatureFormsEntriesAPI>(
    GET_ALL_SIGNATURE_FORMS_ENTRIES_API,
    {
      variables: { contact: parseId(idContact ?? '') } as any,
      fetchPolicy: 'network-only',
      skip: !idContact,
    }
  )

  useEffect(() => {
    if (data?.contactById) {
      setInfoPreview(data.contactById)
    }
  }, [data])

  const policies = useMemo(() => {
    if (!infoPreview) return []
    const map = new Map<
      string,
      {
        policy: any
        relatedMembers: any[]
        allMembers: any[]
      }
    >()

    const upsertMember = (member: any) => {
      if (!member?.policy) return
      const entry = map.get(member.policy.id) || {
        policy: member.policy,
        relatedMembers: [],
        allMembers: [],
      }
      entry.relatedMembers.push(member)
      entry.allMembers.push(member)
      map.set(member.policy.id, entry)
    }

    if (infoPreview.memberPolicy?.policy) {
      upsertMember(infoPreview.memberPolicy)
    }
    infoPreview.members?.edges?.forEach(({ node }) => {
      if (!node?.policy) return
      const entry = map.get(node.policy.id) || {
        policy: node.policy,
        relatedMembers: [],
        allMembers: [],
      }
      entry.relatedMembers.push(node)
      entry.allMembers.push(node)
      map.set(node.policy.id, entry)
    })

    return Array.from(map.values()).map(({ policy, relatedMembers, allMembers }) => {
      const holder =
        policy?.holderMember ||
        allMembers.find((m) => m?.rol === 'HOLDER') ||
        relatedMembers.find((m) => m?.rol === 'HOLDER') ||
        null
      const related = relatedMembers[0] || holder || null
      return { policy, holder, related }
    })
  }, [infoPreview])

  if (!idContact || loading || error || !infoPreview) return null

  return (
    <Modal
      show={true}
      onHide={onClose}
      size='lg'
      backdrop='static'
      keyboard={false}
      dialogClassName='modal-wide-sm modal-preview-contact'
    >
      <Modal.Header closeButton>
        <Modal.Title>Contact Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#f2f3f8' }}>
        {/* Personal Information */}
        <Card>
          <Card.Title className='px-4 mb-0'>
            <div className='kt-heading kt-heading--md custom-border-brand'>
              Personal Information
            </div>
          </Card.Title>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Full Name</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.fullName || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Date of Birth</Form.Label>
                  <p className='text-dark mb-3'>
                    {infoPreview.dateBirth ? moment(infoPreview.dateBirth).format('MM/DD/YYYY') : 'N/A'}
                  </p>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Email</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.email || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Phone</Form.Label>
                  <div className='text-dark mb-3'>
                    <PhoneProviderActions phone={infoPreview.phone} emptyText='N/A' />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>First Name</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.firstName || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Middle Name</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.middleName || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Last Name</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.lastName || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Second Last Name</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.secondLastName || 'N/A'}</p>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Address Information */}
        <Card className='mt-4'>
          <Card.Title className='px-4 mb-0'>
            <div className='kt-heading kt-heading--md custom-border-brand'>
              Address Information
            </div>
          </Card.Title>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Address 1</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.address1 || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Address 2</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.address2 || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>City</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.city || 'N/A'}</p>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>State</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.stateEU?.name || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>Zip Code</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.zipCode || 'N/A'}</p>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className='fw-bold mb-1'>County FIPS</Form.Label>
                  <p className='text-dark mb-3'>{infoPreview.countyFips || 'N/A'}</p>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Policies */}
        <Card className='mt-4'>
          <Card.Title className='px-4 mb-0'>
            <div className='kt-heading kt-heading--md custom-border-brand'>
              Policies
            </div>
          </Card.Title>
          <Card.Body>
            {policies.length === 0 ? (
              <div className='text-muted'>No policies found for this contact.</div>
            ) : (
              <Table responsive size='sm' className='mb-0 align-middle'>
                <thead>
                  <tr>
                    <th>Agent Owner</th>
                    <th>Client</th>
                    <th>Policy</th>
                    <th>Status</th>
                    <th>Agent of Record</th>
                    <th>Role/Relationship</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map(({ policy, holder, related }) => (
                    <tr key={policy.id}>
                      <td>
                        <div>
                          <UserAvatar user={policy.user ?? null} tooltipId={`agent-${policy.id}`} size={40} />
                          <AgentInfoTooltip id={`agent-${policy.id}`} user={policy.user ?? null} />
                        </div>
                      </td>
                      <td>
                        <div style={{ maxWidth: '240px', minWidth: '200px' }}>
                          <div className='text-primary text-link fw-bold' style={{ lineHeight: '1.3' }}>
                            {holder
                              ? `${holder.firstName || ''} ${holder.lastName || ''}`.trim()
                              : (policy.policyName || 'N/A')}
                          </div>
                          <div className='text-muted' style={{ fontSize: '0.9rem' }}>
                            <PhoneProviderActions phone={holder?.phone} emptyText='N/A' />
                          </div>
                          <div className='text-muted' style={{ fontSize: '0.9rem' }}>
                            {policy.stateEU?.code || ''} {policy.zipCode || ''}
                          </div>
                        </div>
                      </td>
                      <td className='limit-width'>
                        <div className='info-policy-name'>
                          <Link
                            className='text-primary text-link fw-bold'
                            to={`/one-policy/${parseId(policy.id)}`}
                            style={{ cursor: 'pointer' }}
                          >
                            {policy.carrier?.name || policy.policyName || 'Policy'}
                          </Link>
                          <p className='m-0 p-0' style={{ fontSize: '0.95rem' }}>
                            Effective:{' '}
                            {policy.effectiveDate
                              ? moment(policy.effectiveDate).format('MMM DD, YYYY')
                              : 'N/A'}
                          </p>
                          <div className='d-flex align-items-center gap-2 mt-1 flex-wrap'>
                            {(() => {
                              const applicantsCount =
                                (policy.applicants && policy.applicants > 0)
                                  ? policy.applicants
                                  : (policy.members ?? 0)
                              return (
                                <Badge
                                  bg='primary'
                                  className='d-flex align-items-center gap-1 text-white'
                                  title={`${applicantsCount} Applicant${applicantsCount !== 1 ? 's' : ''}`}
                                >
                                  <i className='bi bi-people-fill' />
                                  {applicantsCount}
                                </Badge>
                              )
                            })()}
                            {policy.memberId && (
                              <span className='m-0 p-0' style={{ fontSize: '0.9rem' }}>
                                ID: {policy.memberId}
                              </span>
                            )}
                            {policy.hasAutoPolicy === true && (
                              <Badge
                                bg='danger'
                                className='d-flex align-items-center text-white'
                                title='Has auto policy'
                              >
                                <i className='bi bi-car-front-fill' style={{ color: 'var(--bs-white)' }} />
                              </Badge>
                            )}
                            {policy.hasHomePolicy === true && (
                              <Badge
                                bg='success'
                                className='d-flex align-items-center text-white'
                                title='Has home policy'
                              >
                                <i className='bi bi-house-fill' style={{ color: 'var(--bs-white)' }} />
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {policy.status ? (
                          <div className='mb-1'>
                            <BadgeStatePolicy
                              status={getTitleByStatus(
                                dataPoliciesStatuses?.allPolicyStatuses?.edges ?? [],
                                policy.status
                              )}
                              color={getColorByStatus(
                                dataPoliciesStatuses?.allPolicyStatuses?.edges ?? [],
                                policy.status
                              )}
                            />
                          </div>
                        ) : (
                          <div className='text-muted mb-1'>N/A</div>
                        )}
                        {policy.stateDocs ? (
                          <div className='mb-1'>
                            <span className='text-muted me-1'>Documents:</span>
                            <Badge className='badge-sm' bg={getDocumentVariant(policy.stateDocs)}>
                              {policy.stateDocs}
                            </Badge>
                          </div>
                        ) : null}
                        {policy.paymentStatus ? (
                          <div>
                            <span className='text-muted me-1'>Payment:</span>
                            <Badge
                              className='badge-sm'
                              bg={getPaymentVariant(policy.paymentStatus)}
                              text={getPaymentVariant(policy.paymentStatus) === 'danger' ? 'white' : undefined}
                            >
                              {policy.paymentStatus}
                            </Badge>
                          </div>
                        ) : null}
                      </td>
                      <td>{policy.agentOfRecord || 'N/A'}</td>
                      <td>
                        <div className='fw-bold'>{related?.rol || 'N/A'}</div>
                        {related?.rol && related.rol !== 'HOLDER' ? (
                          <Badge bg='dark' text='white' className='badge-sm'>
                            {related.relationship || 'N/A'}
                          </Badge>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>


        {/* Signature Forms */}
        <Card className='mt-4'>
          <Card.Title className='px-4 mb-0'>
            <div className='kt-heading kt-heading--md custom-border-brand'>
              Signature Forms
            </div>
          </Card.Title>
          <Card.Body>
            <SignatureFormsEntriesTimeline
              loading={loadingEntries}
              dataEntriesByLead={dataEntries?.allSignatureFormsEntries}
              refetch={refetchEntries}
              hasEmail={Boolean(infoPreview?.email && infoPreview.email.trim() !== '')}
              hasPhone={Boolean(infoPreview?.phone && infoPreview.phone.trim() !== '')}
              compact
            />
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
